/**
 * Brand-owner toggle for whether the public site has customer accounts.
 * Saves through the existing updateBrandData endpoint — backend sanitises
 * the per-brand boolean fields onto the brand_profile document.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

export default function CustomerLoginToggle({ brandData, fetchData }) {
  const brandId = brandData?._id;
  const [loginOn, setLoginOn] = useState(false);
  const [signupOn, setSignupOn] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (brandData) {
      setLoginOn(!!brandData.customer_login_enabled);
      setSignupOn(brandData.customer_self_signup_enabled !== false);
    }
  }, [brandData]);

  const save = (next) => {
    if (!brandId) return;
    setSaving(true);
    const payload = {
      brand_id: brandId,
      customer_login_enabled: next.loginOn,
      customer_self_signup_enabled: next.signupOn,
    };
    const fd = new FormData();
    fd.append('data', JSON.stringify(payload));
    API.updateBrandData(fd)
      .then(() => { toast.success('Saved'); fetchData && fetchData(); })
      .catch((err) => toast.error(err?.message || 'Failed'))
      .finally(() => setSaving(false));
  };

  const handleLogin = (val) => { setLoginOn(val); save({ loginOn: val, signupOn }); };
  const handleSignup = (val) => { setSignupOn(val); save({ loginOn, signupOn: val }); };

  if (!brandId) return null;

  return (
    <Card sx={{ borderRadius: '20px' }}>
      <CardContent>
        <Box mb='14px' ml='10px'>
          <Typography variant='h6' fontWeight='bold'>Customer accounts</Typography>
          <Typography variant='body2' color='text.secondary'>
            Let your customers sign in to your brand site so they can see their orders, save addresses, and have their own profile — separate from any other brand they may use.
          </Typography>
        </Box>
        <Box mx='10px'>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            py='10px'
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Box>
              <Typography fontWeight='600'>Enable customer login</Typography>
              <Typography variant='body2' color='text.secondary'>
                When off, the public site is browse-only. Customers won't see a login link.
              </Typography>
            </Box>
            <Switch
              checked={loginOn}
              onChange={(e) => handleLogin(e.target.checked)}
              disabled={saving}
              size='medium'
            />
          </Stack>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            py='10px'
          >
            <Box>
              <Typography fontWeight='600'>Allow self sign-up</Typography>
              <Typography variant='body2' color='text.secondary'>
                When off, only people you've already invited can sign in. Useful for private / wholesale stores.
              </Typography>
            </Box>
            <Switch
              checked={signupOn}
              onChange={(e) => handleSignup(e.target.checked)}
              disabled={!loginOn || saving}
              size='medium'
            />
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
